import React, { useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';

import {
  EventCard,
  CardFirstRow,
  CardSecondRow,
  CardThirdRow,
  CardFourthRow,
  LineSeparetor,
  CardFifthRow,
  ExecPhoto,
  ExecPhotoWrapper,
  MoreExecContainer,
  StatusIcon,
} from '../shared/styled';
import { ReactComponent as PendingAgIcon } from 'assets/icons/pending-ag-icon.svg';
import { ReactComponent as CheckGIcon } from 'assets/icons/check-g.svg';
import { ReactComponent as ExecutorNoPhotoIcon } from 'assets/icons/executor-no-photo-icon.svg';
import { CalendarEvent, EventColorSet } from 'pages/Agenda/utils';
import { useEventCardDisplay } from '../../../hooks/useEventCardDisplay';

interface DraggableEventProps {
  event: CalendarEvent;
  onHover: (data: { event: CalendarEvent; rect: DOMRect; colorSet: EventColorSet }) => void;
  onLeave: () => void;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  isDragging: boolean;
  colorSet: EventColorSet;
  isCompletedView: boolean;
}

export const DraggableEvent: React.FC<DraggableEventProps> = ({
  event,
  onHover,
  onLeave,
  onCardClick,
  isDragging,
  colorSet,
  isCompletedView,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: event.id,
    disabled: isCompletedView,
  });
  const { atividadesLabel, executorCount, firstExecutor, displayExecutors } = useEventCardDisplay(event);

  const style: React.CSSProperties = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    zIndex: transform ? 99 : 'auto',
    cursor: isCompletedView ? 'pointer' : transform ? 'grabbing' : isDragging ? 'default' : 'grab',
  };

  const handleClick = useCallback(() => {
    if (isDragging) return;
    onCardClick(event, colorSet);
  }, [isDragging, event, colorSet, onCardClick]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      onHover({ event, rect, colorSet });
    },
    [isDragging, event, colorSet, onHover],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      onHover({ event, rect, colorSet });
    },
    [isDragging, event, colorSet, onHover],
  );

  const handleRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
    },
    [setNodeRef],
  );

  return (
    <EventCard
      ref={handleRef}
      {...listeners}
      {...attributes}
      style={style}
      borderLeft={colorSet.borderColor}
      backgroundColor={colorSet.backgroundColor}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={onLeave}
      data-event-id={event.id}
    >
      <CardFirstRow fontColor={colorSet.fontColor}>
        <span>
          {event.time} • {event.title}
        </span>
        <StatusIcon color="transparent">
          {event.realizada || isCompletedView ? (
            <CheckGIcon width={16} height={16} />
          ) : event.atrasada ? (
            <PendingAgIcon width={12} height={12} />
          ) : null}
        </StatusIcon>
      </CardFirstRow>
      <CardSecondRow>{event.aplicacao}</CardSecondRow>
      <CardThirdRow>{event.equipamento}</CardThirdRow>
      <CardFourthRow>
        {executorCount === 1 && firstExecutor ? (
          <>
            {firstExecutor.avatar ? (
              <ExecPhoto src={firstExecutor.avatar} alt={firstExecutor.nome} />
            ) : (
              <ExecPhotoWrapper>
                <ExecutorNoPhotoIcon width={20} height={20} />
              </ExecPhotoWrapper>
            )}
            <span
              style={{
                marginLeft: '6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {firstExecutor.nome}
            </span>
          </>
        ) : (
          <MoreExecContainer>
            <div>
              {displayExecutors.map((exec, index) =>
                exec.avatar ? (
                  <ExecPhoto
                    key={`exec-${exec.nome}-${index}`}
                    src={exec.avatar}
                    alt={exec.nome}
                    style={{
                      marginLeft: index === 0 ? 0 : -8,
                      zIndex: index,
                      position: 'relative',
                    }}
                  />
                ) : (
                  <ExecPhotoWrapper
                    key={`exec-${exec.nome}-${index}`}
                    style={{
                      marginLeft: index === 0 ? 0 : -8,
                      zIndex: index,
                      position: 'relative',
                    }}
                  >
                    <ExecutorNoPhotoIcon width={20} height={20} />
                  </ExecPhotoWrapper>
                ),
              )}
              <span>{executorCount} executores</span>
            </div>
          </MoreExecContainer>
        )}
      </CardFourthRow>
      <LineSeparetor separetorColor={colorSet.separetorColor} />
      <CardFifthRow>{atividadesLabel}</CardFifthRow>
    </EventCard>
  );
};
